/**
 * Mentoria Lei Seca - Main JavaScript
 * @package MentoriaLeiSeca
 */

// Import components
import initProductModules, { toggleModuleByIndex, handleModuleKeydown } from './components/ProductModules.js';
import initProductFaq, { toggleFaqByIndex } from './components/ProductFaq.js';
import initGradeModal from './components/GradeModal.js';

// Swiper is loaded via CDN in functions.php
// import Swiper from 'swiper';
// import { Navigation, Pagination } from 'swiper/modules';
// import 'swiper/css'; ...

// Expose module functions globally for inline PHP usage
window.toggleModule = toggleModuleByIndex;
window.handleModuleKeydown = handleModuleKeydown;

// Expose FAQ functions globally for inline PHP usage
window.toggleFaqItem = toggleFaqByIndex;

(function () {
    'use strict';

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function initApp() {
        initMobileMenu();
        initSmoothScroll();
        initHeaderScroll();
        initValidationLogosAnimation();
        initAboutCarousel();
        initCartModal();
        initContactForm();
        initTestimonialsCarousel();
        initProductNavigation();
        initDescriptionToggle();
        initProductModules();
        initProductFaq();
        initProductCarousel();
        initGradeModal();
    }

    // DOM Ready (supports deferred/delayed scripts)
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initApp);
    } else {
        initApp();
    }

    /**
     * Product Carousel (Swiper) for Home Page
     */
    function initProductCarousel() {
        const productCarousels = document.querySelectorAll('.home-products-carousel');

        if (prefersReducedMotion || productCarousels.length === 0 || typeof Swiper === 'undefined') {return;}

        productCarousels.forEach(carousel => {
            new Swiper(carousel, {
                // modules: [Navigation, Pagination], // Included in bundle
                slidesPerView: 1,
                spaceBetween: 20,
                breakpoints: {
                    320: {
                        slidesPerView: 1.08,
                        spaceBetween: 12
                    },
                    576: {
                        slidesPerView: 1.35,
                        spaceBetween: 16
                    },
                    768: {
                        slidesPerView: 2,
                        spaceBetween: 20
                    },
                    992: {
                        slidesPerView: 3,
                        spaceBetween: 24
                    },
                    1168: {
                        slidesPerView: 4,
                        spaceBetween: 24
                    }
                },
                navigation: {
                    nextEl: carousel.querySelector('.swiper-button-next'),
                    prevEl: carousel.querySelector('.swiper-button-prev'),
                },
                pagination: {
                    el: carousel.querySelector('.swiper-pagination'),
                    clickable: true,
                }
            });
        });
    }

    /**
     * Mobile Menu Toggle
     */
    function initMobileMenu() {
        const menuToggles = document.querySelectorAll('.mobile-menu-toggle');
        const mobileMenu = document.querySelector('.mobile-menu');
        const body = document.body;
        let lastFocusedElement = null;

        if (menuToggles.length === 0 || !mobileMenu) {return;}

        function closeMenu() {
            menuToggles.forEach(btn => {
                btn.setAttribute('aria-expanded', 'false');
                const lines = btn.querySelectorAll('.hamburger-line');
                if (lines.length >= 3) {
                    lines[0].style.transform = '';
                    lines[1].style.opacity = '';
                    lines[2].style.transform = '';
                }
            });
            mobileMenu.setAttribute('aria-hidden', 'true');
            body.classList.remove('mobile-menu-open');

            if (lastFocusedElement) {
                lastFocusedElement.focus();
                lastFocusedElement = null;
            }
        }

        menuToggles.forEach(function (menuToggle) {
            menuToggle.addEventListener('click', function () {
                // Verifica se o menu está escondido
                const isCurrentlyHidden = mobileMenu.getAttribute('aria-hidden') !== 'false';
                // Se está escondido, vamos abrir (shouldOpen = true)
                const shouldOpen = isCurrentlyHidden;

                // Update ALL toggles to match state
                menuToggles.forEach(btn => {
                    btn.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
                    // Update hamburger animation for all
                    const lines = btn.querySelectorAll('.hamburger-line');
                    if (lines.length >= 3) {
                        if (shouldOpen) {
                            lines[0].style.transform = 'rotate(45deg) translate(5px, 5px)';
                            lines[1].style.opacity = '0';
                            lines[2].style.transform = 'rotate(-45deg) translate(7px, -6px)';
                        } else {
                            lines[0].style.transform = '';
                            lines[1].style.opacity = '';
                            lines[2].style.transform = '';
                        }
                    }
                });

                mobileMenu.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');
                if (shouldOpen) {
                    lastFocusedElement = document.activeElement;
                    body.classList.add('mobile-menu-open');
                    const firstLink = mobileMenu.querySelector('a');
                    if (firstLink) {
                        firstLink.focus();
                    }
                } else {
                    closeMenu();
                }
            });
        });


        // Close menu on link click
        mobileMenu.querySelectorAll('a').forEach(function (link) {
            link.addEventListener('click', function () {
                closeMenu();
            });
        });

        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && mobileMenu.getAttribute('aria-hidden') === 'false') {
                closeMenu();
            }
        });
    }


    /**
     * Smooth Scroll for anchor links
     */
    function initSmoothScroll() {
        document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
            anchor.addEventListener('click', function (e) {
                const targetId = this.getAttribute('href');
                if (targetId === '#') {return;}

                const target = document.querySelector(targetId);
                if (target) {
                    e.preventDefault();
                    const header = document.querySelector('.site-header');
                    const headerHeight = header ? header.offsetHeight : 0;
                    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;

                    window.scrollTo({
                        top: targetPosition,
                        behavior: prefersReducedMotion ? 'auto' : 'smooth'
                    });
                }
            });
        });
    }

    /**
     * Header scroll effect
     */
    function initHeaderScroll() {
        const header = document.querySelector('.hero-header');
        if (!header) {return;}

        let lastScroll = 0;
        const scrollThreshold = 10;

        window.addEventListener('scroll', function () {
            const currentScroll = window.pageYOffset;

            // Add scrolled class for styling
            if (currentScroll > scrollThreshold) {
                header.classList.add('scrolled');
            } else {
                header.classList.remove('scrolled');
            }

            lastScroll = currentScroll;
        }, { passive: true });
    }

    /**
     * Validation logos infinite scroll animation
     */
    function initValidationLogosAnimation() {
        const track = document.querySelector('.validation-logos-track');
        if (!track || prefersReducedMotion) {return;}

        // Clone items for infinite scroll
        const items = track.querySelectorAll('.validation-logo');
        items.forEach(function (item) {
            const clone = item.cloneNode(true);
            track.appendChild(clone);
        });
    }

    /**
     * Text scroll infinite loop
     */
    function initAboutCarousel() {
        const container = document.querySelector('.text-scroll');
        if (!container || prefersReducedMotion) {return;}

        const track = container.querySelector('.text-scroll__track');
        const item = container.querySelector('.text-scroll__item');
        if (!track || !item || track.dataset.initialized === 'true') {return;}
        track.dataset.initialized = 'true';

        document.fonts.ready.then(function () {
            const itemWidth = item.offsetWidth;
            if (itemWidth === 0) {return;}

            // Clone items to fill screen + extra for loop
            const containerWidth = container.offsetWidth;
            const clonesNeeded = Math.ceil(containerWidth / itemWidth) + 2;

            for (let i = 0; i < clonesNeeded; i++) {
                track.appendChild(item.cloneNode(true));
            }

            let position = 0;
            const speed = 0.5;

            function animate() {
                position -= speed;
                if (Math.abs(position) >= itemWidth) {
                    position = 0;
                }
                track.style.transform = 'translateY(-50%) translateX(' + position + 'px)';
                requestAnimationFrame(animate);
            }

            animate();
        });
    }

    function getFocusableElements(container) {
        return Array.from(container.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ));
    }

    function trapFocus(modal) {
        const focusable = getFocusableElements(modal);
        if (focusable.length === 0) {
            return function () {};
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        function handleKeydown(e) {
            if (e.key !== 'Tab') {return;}

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }

        modal.addEventListener('keydown', handleKeydown);

        return function () {
            modal.removeEventListener('keydown', handleKeydown);
        };
    }

    /**
     * Cart Modal
     */
    function initCartModal() {
        const triggers = document.querySelectorAll('.cart-modal-trigger');

        const headerCartModal = document.getElementById('cartModalHero');
        if (triggers.length === 0 && !headerCartModal) {return;}

        function dedupeNoticeForwardButtons() {
            const notices = document.querySelectorAll(
                '.woocommerce-notices-wrapper .woocommerce-message, .woocommerce-notices-wrapper .woocommerce-error, .woocommerce-notices-wrapper .woocommerce-info'
            );

            notices.forEach(function (notice) {
                const forwardButtons = notice.querySelectorAll('.wc-forward');
                forwardButtons.forEach(function (btn, index) {
                    if (index > 0) {
                        btn.remove();
                    }
                });
            });
        }

        // Some WooCommerce flows can output duplicated wc-forward buttons in the same notice.
        // Keep only the first one, and re-run when notices are injected dynamically.
        dedupeNoticeForwardButtons();
        const noticesWrapper = document.querySelector('.woocommerce-notices-wrapper');
        if (noticesWrapper && typeof MutationObserver !== 'undefined') {
            const observer = new MutationObserver(function () {
                dedupeNoticeForwardButtons();
            });
            observer.observe(noticesWrapper, { childList: true, subtree: true });
        }

        function openModal(modal, trigger) {
            modal.setAttribute('aria-hidden', 'false');
            if (trigger) {
                trigger.setAttribute('aria-expanded', 'true');
                modal.__lastFocusedTrigger = trigger;
            }
            document.body.style.overflow = 'hidden';

            const focusable = getFocusableElements(modal);
            if (focusable.length > 0) {
                focusable[0].focus();
            } else {
                const content = modal.querySelector('.cart-modal-content');
                if (content) {content.focus();}
            }

            if (modal.__removeFocusTrap) {
                modal.__removeFocusTrap();
            }
            modal.__removeFocusTrap = trapFocus(modal);
        }

        function closeModal(modal) {
            if (!modal) {return;}

            modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';

            if (modal.__removeFocusTrap) {
                modal.__removeFocusTrap();
                modal.__removeFocusTrap = null;
            }

            const trigger = modal.__lastFocusedTrigger;
            if (trigger) {
                trigger.setAttribute('aria-expanded', 'false');
                trigger.focus();
            }
        }

        // Open modal (header trigger)
        triggers.forEach(function (trigger) {
            trigger.addEventListener('click', function (e) {
                e.preventDefault();
                e.stopPropagation();

                // Encontrar o modal mais próximo (dentro do mesmo cart-trigger-wrapper)
                const wrapper = trigger.closest('.cart-trigger-wrapper');
                if (wrapper) {
                    const modal = wrapper.querySelector('.cart-modal');
                    if (modal) {
                        openModal(modal, trigger);
                    }
                }
            });
        });

        // Close buttons
        const closeButtons = document.querySelectorAll('.cart-modal-close');
        closeButtons.forEach(function (closeBtn) {
            closeBtn.addEventListener('click', function (e) {
                e.stopPropagation();
                const modal = closeBtn.closest('.cart-modal');
                closeModal(modal);
            });
        });

        // Close on ESC key
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') {
                const openModals = document.querySelectorAll('.cart-modal[aria-hidden="false"]');
                openModals.forEach(function (modal) {
                    closeModal(modal);
                });
            }
        });

        // Close when clicking on overlay
        document.addEventListener('click', function (e) {
            const openModals = document.querySelectorAll('.cart-modal[aria-hidden="false"]');
            openModals.forEach(function (modal) {
                const overlay = modal.querySelector('.cart-modal-overlay');
                if (overlay && e.target === overlay) {
                    closeModal(modal);
                }
            });
        });

        // Intercept explicit modal triggers only (do not hijack WooCommerce wc-forward links)
        document.addEventListener('click', function (e) {
            const trigger = e.target.closest('.js-open-cart-modal');
            if (!trigger) {return;}

            e.preventDefault();
            e.stopPropagation();

            // Find the header cart modal (cartModalHero)
            const modal = headerCartModal || document.getElementById('cartModalHero');
            if (modal) {
                openModal(modal, trigger);
            }
        });

        // Handle coupon form submission via AJAX
        const couponForms = document.querySelectorAll('.cart-modal-coupon');
        couponForms.forEach(function (couponForm) {
            const form = couponForm.closest('form');
            if (!form) {return;}

            const couponInput = couponForm.querySelector('input[name="coupon_code"]');
            const couponButton = couponForm.querySelector('button[name="apply_coupon"]');

            if (couponButton && form) {
                couponButton.addEventListener('click', function (e) {
                    e.preventDefault();
                    e.stopPropagation();

                    const couponCode = couponInput ? couponInput.value.trim() : '';
                    if (!couponCode) {
                        alert('Por favor, insira um código de cupom.');
                        return;
                    }

                    // Show loading state
                    couponButton.disabled = true;
                    const originalText = couponButton.textContent;
                    couponButton.textContent = 'Aplicando...';

                    // Get nonce
                    const nonce = form.querySelector('input[name="woocommerce-cart-nonce"]')?.value || '';

                    // Prepare form data
                    const formData = new FormData();
                    formData.append('coupon_code', couponCode);
                    formData.append('apply_coupon', 'Aplicar cupom');
                    formData.append('woocommerce-cart-nonce', nonce);

                    // Submit to cart page via AJAX
                    fetch(form.action || '/carrinho/', {
                        method: 'POST',
                        body: formData,
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    })
                        .then(function (response) {
                            return response.text();
                        })
                        .then(function (html) {
                            // Parse response
                            const parser = new DOMParser();
                            const doc = parser.parseFromString(html, 'text/html');

                            const modal = form.closest('.cart-modal');
                            if (!modal) {return;}

                            // Update footer totals
                            const footer = modal.querySelector('.cart-modal-footer');
                            const newFooter = doc.querySelector('.cart-modal-footer');
                            if (footer && newFooter) {
                                footer.innerHTML = newFooter.innerHTML;
                            }

                            // Show notices
                            const notices = doc.querySelector('.woocommerce-message, .woocommerce-error, .woocommerce-info');
                            if (notices) {
                                // Remove existing notices
                                const existingNotices = couponForm.querySelector('.woocommerce-message, .woocommerce-error, .woocommerce-info');
                                if (existingNotices) {
                                    existingNotices.remove();
                                }

                                // Clone notice
                                const noticeClone = notices.cloneNode(true);

                                // Insert notice at the beginning of coupon form
                                couponForm.insertBefore(noticeClone, couponForm.firstChild);
                            }

                            // Clear input on success
                            const successMessage = doc.querySelector('.woocommerce-message');
                            if (successMessage) {
                                couponInput.value = '';
                            }
                        })
                        .catch(function (error) {
                            console.error('Erro ao aplicar cupom:', error);
                            alert('Erro ao aplicar cupom. Por favor, tente novamente.');
                        })
                        .finally(function () {
                            couponButton.disabled = false;
                            couponButton.textContent = originalText;
                        });
                });
            }
        });
    }

    /**
     * Contact Form Handler
     */
    function initContactForm() {
        const contactForm = document.getElementById('contactForm');
        if (!contactForm) {return;}

        const submitButton = document.getElementById('submitButton');
        const messagesContainer = document.getElementById('formMessages');

        if (!submitButton || !messagesContainer) {return;}

        const buttonSpan = submitButton.querySelector('span');
        const originalButtonText = buttonSpan ? buttonSpan.textContent : 'Enviar mensagem';

        contactForm.addEventListener('submit', function (e) {
            e.preventDefault();

            // Limpar mensagens anteriores
            messagesContainer.innerHTML = '';

            // Desabilitar botão
            submitButton.disabled = true;
            if (buttonSpan) {
                buttonSpan.textContent = 'Enviando...';
            }

            // Coletar dados do formulário
            const formData = new FormData(contactForm);
            formData.append('action', 'mls_contact_form');

            // Enviar via AJAX
            fetch(mls_ajax.ajax_url, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })
                .then(function (response) {
                    return response.json();
                })
                .then(function (data) {
                    // Mostrar mensagem
                    const messageDiv = document.createElement('div');
                    messageDiv.className = 'form-message';

                    if (data.success) {
                        messageDiv.classList.add('form-message-success');
                        messageDiv.textContent = data.data.message;
                        contactForm.reset();
                    } else {
                        messageDiv.classList.add('form-message-error');
                        messageDiv.textContent = data.data.message;
                    }

                    messagesContainer.appendChild(messageDiv);

                    // Scroll para mensagem
                    messagesContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                })
                .catch(function (error) {
                    const messageDiv = document.createElement('div');
                    messageDiv.className = 'form-message form-message-error';
                    messageDiv.textContent = 'Erro ao enviar mensagem. Por favor, tente novamente.';
                    messagesContainer.appendChild(messageDiv);
                })
                .finally(function () {
                    // Reabilitar botão
                    submitButton.disabled = false;
                    if (buttonSpan) {
                        buttonSpan.textContent = originalButtonText;
                    }
                });
        });
    }

    /**
     * Testimonials Carousel - Loop infinito mostrando até 3 imagens por vez
     */
    function initTestimonialsCarousel() {
        const grid = document.querySelector('.testimonials-grid.testimonials-carousel');
        if (!grid || prefersReducedMotion) {return;}

        const cards = grid.querySelectorAll('.testimonial-card');
        const totalCards = cards.length;
        if (totalCards <= 3) {return;} // Não precisa de carrossel se tiver 3 ou menos

        // Calcular largura real do card (incluindo padding e border)
        const firstCard = cards[0];
        const cardRect = firstCard.getBoundingClientRect();
        const cardWidth = cardRect.width;
        const gap = 24;
        const step = cardWidth + gap;

        // Clonar todos os cards para criar loop infinito
        const cardsArray = Array.from(cards);
        cardsArray.forEach(function (card) {
            const clone = card.cloneNode(true);
            grid.appendChild(clone);
        });

        // Clonar mais uma vez para garantir transição suave
        cardsArray.forEach(function (card) {
            const clone = card.cloneNode(true);
            grid.appendChild(clone);
        });

        let currentIndex = 0;
        let intervalId = null;

        function moveCarousel() {
            currentIndex++;
            const maxIndex = totalCards;

            // Se chegou ao final do primeiro conjunto, resetar sem animação
            if (currentIndex >= maxIndex) {
                currentIndex = 0;
                grid.style.transition = 'none';
                grid.style.transform = 'translateX(0)';
                // Força reflow
                void grid.offsetWidth;
                grid.style.transition = 'transform 0.6s ease-in-out';
            }

            grid.style.transform = `translateX(-${currentIndex * step}px)`;
        }

        function startCarousel() {
            if (intervalId) {clearInterval(intervalId);}
            intervalId = setInterval(moveCarousel, 3000); // Muda a cada 3 segundos
        }

        function stopCarousel() {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        }

        // Iniciar carrossel
        startCarousel();

        // Pausar no hover
        grid.addEventListener('mouseenter', stopCarousel);
        grid.addEventListener('mouseleave', startCarousel);
    }

    /**
     * Product Navigation - Scroll Suave e Highlight Ativo
     * Requirements: 2.2, 2.3
     */
    function initProductNavigation() {
        const nav = document.getElementById('productNav');
        const links = document.querySelectorAll('.product-nav__link');

        if (!nav || links.length === 0) {return;}

        const sections = [];
        let navHeight = nav.offsetHeight;
        const scrollOffset = 20;

        // Cachear seções
        links.forEach(function (link) {
            const sectionId = link.getAttribute('data-section');
            if (sectionId) {
                const section = document.getElementById(sectionId);
                if (section) {
                    sections.push({
                        id: sectionId,
                        element: section,
                        link: link
                    });
                }
            }
        });

        // Requirement 2.2: Scroll suave ao clicar nos links
        links.forEach(function (link) {
            link.addEventListener('click', function (e) {
                e.preventDefault();

                const sectionId = link.getAttribute('data-section');
                const targetSection = document.getElementById(sectionId);

                if (!targetSection) {return;}

                // Calcular posição de destino
                const targetPosition = targetSection.getBoundingClientRect().top
                    + window.pageYOffset
                    - navHeight
                    - scrollOffset;

                // Scroll suave
                window.scrollTo({
                    top: targetPosition,
                    behavior: prefersReducedMotion ? 'auto' : 'smooth'
                });

                // Atualizar link ativo
                setActiveLink(link);
            });
        });

        // Adicionar classe scrolled quando scrollar
        window.addEventListener('scroll', function () {
            if (window.scrollY > 100) {
                nav.classList.add('scrolled');
            } else {
                nav.classList.remove('scrolled');
            }
        }, { passive: true });

        // Requirement 2.3: IntersectionObserver para destacar link ativo
        const observerOptions = {
            root: null,
            rootMargin: '-' + (navHeight + 50) + 'px 0px -50% 0px',
            threshold: 0
        };

        const observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                    const sectionId = entry.target.id;
                    const sectionData = sections.find(function (s) {
                        return s.id === sectionId;
                    });

                    if (sectionData) {
                        setActiveLink(sectionData.link);
                    }
                }
            });
        }, observerOptions);

        // Observar todas as seções
        sections.forEach(function (section) {
            observer.observe(section.element);
        });

        // Função para definir link ativo
        function setActiveLink(activeLink) {
            links.forEach(function (link) {
                link.classList.remove('active');
                link.removeAttribute('aria-current');
            });

            if (activeLink) {
                activeLink.classList.add('active');
                activeLink.setAttribute('aria-current', 'true');
            }
        }

        // Recalcular altura da nav em resize
        window.addEventListener('resize', function () {
            navHeight = nav.offsetHeight;
        });
    }

    /**
     * Description Toggle - Ler mais/Ler menos
     * Requirements: 4.3, 4.4
     */
    function initDescriptionToggle() {
        const textElement = document.getElementById('descriptionText');
        const toggleButton = document.getElementById('readMoreBtn');

        if (!textElement || !toggleButton) {return;}

        // Verificar se o texto precisa de truncamento
        const lineHeight = parseFloat(getComputedStyle(textElement).lineHeight);
        const maxHeight = lineHeight * 4; // 4 linhas

        // Se o conteúdo é menor que 4 linhas, esconder o botão
        if (textElement.scrollHeight <= maxHeight + 10) {
            toggleButton.style.display = 'none';
            textElement.classList.add('expanded');
            return;
        }

        // Requirement 4.4: Toggle ao clicar
        toggleButton.addEventListener('click', function () {
            const isExpanded = textElement.classList.contains('expanded');

            if (isExpanded) {
                // Colapsar
                textElement.classList.remove('expanded');
                toggleButton.textContent = 'Ler mais';
            } else {
                // Expandir
                textElement.classList.add('expanded');
                toggleButton.textContent = 'Ler menos';
            }
        });
    }

})();
